import React, { useState } from "react";
import {
  BookOpen,
  Plus,
  X,
  Edit2,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ArrowUpDown,
  Search,
  History,
  Send,
  Users,
  Landmark,
  GraduationCap,
} from "lucide-react";
import { formatDisplayDate } from "../utils/dateUtils";
import { renderRichText } from "../utils/richText";
import { renderMarkdownContent, MARKDOWN_HINT } from "../utils/markdownContent";
import { ATTEMPT_KEYS, extractAttempt } from "../utils/publications";

// Workflow-ordered statuses. `id` is what gets stored/synced.
export const PUB_STATUSES = [
  {
    id: "drafting",
    label: "Drafting",
    badge: "bg-gray-500/15 text-gray-300 border-gray-500/40",
  },
  {
    id: "submitted",
    label: "Submitted",
    badge: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  },
  {
    id: "awaiting-decision",
    label: "Awaiting Decision",
    badge: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  },
  {
    id: "revision",
    label: "Revision",
    badge: "bg-orange-500/15 text-orange-300 border-orange-500/40",
  },
  {
    id: "rejected",
    label: "Rejected",
    badge: "bg-red-500/15 text-red-300 border-red-500/40",
  },
  {
    id: "accepted",
    label: "Accepted",
    badge: "bg-green-500/15 text-green-300 border-green-500/40",
  },
  {
    id: "published",
    label: "Published",
    badge: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  },
];

const statusMeta = (id) =>
  PUB_STATUSES.find((s) => s.id === id) || PUB_STATUSES[0];

const SORT_MODES = [
  { id: "status", label: "Status" },
  { id: "submitted", label: "Submission date" },
  { id: "modified", label: "Date modified" },
  { id: "added", label: "Date added" },
];

// A blank submission attempt (the venue-specific + date fields).
export const blankAttempt = () => ({
  venueType: "conference",
  conferenceName: "",
  conferenceLocation: "",
  conferenceDateFrom: null,
  conferenceDateTo: null,
  journalName: "",
  publisher: "",
  impactFactor: "",
  quartile: "",
  submittedDate: null,
  expectedDecisionDate: null,
  decisionDate: null,
  cameraReadyDate: null,
  publishedDate: null,
  attemptNotes: "",
});

export const newPublication = (title, shortName) => ({
  id: Date.now() + Math.floor(Math.random() * 100000),
  title: title || "",
  shortName: shortName || "",
  authors: "",
  status: "drafting",
  revisionNumber: "",
  comments: "",
  doiUrl: "",
  preprintUrl: "",
  ...blankAttempt(),
  history: [],
  order: 0,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const normalizeUrl = (raw) => {
  const url = (raw || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

const venueName = (a) =>
  a.venueType === "journal"
    ? (a.journalName || "").trim()
    : (a.conferenceName || "").trim();

// Header shows the short name when present; otherwise the full title.
const headerName = (p) =>
  (p.shortName || "").trim() || (p.title || "").trim() || "Untitled Paper";
// The full title only shown as a secondary line when a short name exists AND
// differs from the full title (avoids showing the same text twice).
const secondaryTitle = (p) => {
  const short = (p.shortName || "").trim();
  const full = (p.title || "").trim();
  return short && full && short !== full ? full : "";
};

const fmt = (d) => (d ? formatDisplayDate(new Date(d)) : "");

const sortPublications = (pubs, mode) => {
  const arr = [...pubs];
  const statusRank = (p) =>
    PUB_STATUSES.findIndex((s) => s.id === p.status) === -1
      ? 0
      : PUB_STATUSES.findIndex((s) => s.id === p.status);
  const ts = (v) => (v ? new Date(v).getTime() : 0);
  switch (mode) {
    case "submitted":
      // Newest submission first; papers without a date sink to the bottom.
      return arr.sort((a, b) => ts(b.submittedDate) - ts(a.submittedDate));
    case "modified":
      return arr.sort((a, b) => ts(b.updatedAt) - ts(a.updatedAt));
    case "added":
      return arr.sort(
        (a, b) =>
          (a.order ?? 0) - (b.order ?? 0) || ts(a.createdAt) - ts(b.createdAt),
      );
    case "status":
    default:
      return arr.sort(
        (a, b) =>
          statusRank(a) - statusRank(b) || ts(b.updatedAt) - ts(a.updatedAt),
      );
  }
};

const Publications = ({
  publications = [],
  sortMode = "status",
  onSortModeChange,
  onAddPublication,
  onUpdatePublication,
  onDeletePublication,
  onMovePanelUp,
  onMovePanelDown,
  isFirstPanel,
  isLastPanel,
}) => {
  const setSortMode = onSortModeChange || (() => {});
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newShortName, setNewShortName] = useState("");
  const [expanded, setExpanded] = useState({});
  const [historyOpen, setHistoryOpen] = useState({});
  // editing = null | { pubId, scope: "paper" | "attempt", index? }
  //  - "paper"  edits the current attempt + shared paper-level fields
  //  - "attempt" edits a single previous attempt (history[index]); shared
  //             paper-level fields are hidden since those belong to the paper.
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  // Set of status ids to filter by. Empty = show all. Click a top tag to toggle.
  const [statusFilter, setStatusFilter] = useState(() => new Set());

  const toggleStatusFilter = (id) =>
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleHistory = (id) =>
    setHistoryOpen((prev) => ({ ...prev, [id]: !prev[id] }));

  // ---- add ----
  // At least one of full paper name / short name is required.
  const handleAdd = () => {
    const title = newTitle.trim();
    const shortName = newShortName.trim();
    if (!title && !shortName) return;
    const pub = {
      ...newPublication(title, shortName),
      order: publications.length,
    };
    onAddPublication(pub);
    setNewTitle("");
    setNewShortName("");
    setShowAdd(false);
    setExpanded((prev) => ({ ...prev, [pub.id]: true }));
    startEdit(pub);
  };

  // ---- edit (current attempt + paper-level fields) ----
  const startEdit = (pub) => {
    setEditing({ pubId: pub.id, scope: "paper" });
    setDraft({ ...pub });
  };
  // ---- edit a single previous attempt (per-attempt fields only) ----
  const startEditHistory = (pub, index) => {
    const entry = (pub.history || [])[index];
    if (!entry) return;
    setEditing({ pubId: pub.id, scope: "attempt", index });
    setDraft({ ...extractAttempt(entry) });
  };
  const cancelEdit = () => {
    setEditing(null);
    setDraft(null);
  };
  const saveEdit = () => {
    if (!draft || !editing) return;
    if (editing.scope === "attempt") {
      // Replace just this history entry with the edited attempt. Resolution in
      // App may then promote it to current if it now outranks the others.
      const pub = publications.find((p) => p.id === editing.pubId);
      if (!pub) return cancelEdit();
      const edited = extractAttempt(draft);
      const attempt = {};
      for (const k of ATTEMPT_KEYS) attempt[k] = edited[k];
      const orig = (pub.history || [])[editing.index] || {};
      const nextEntry = { ...attempt, archivedAt: orig.archivedAt || null };
      const history = (pub.history || []).map((h, i) =>
        i === editing.index ? nextEntry : h,
      );
      onUpdatePublication(pub.id, { history });
      return cancelEdit();
    }
    const { id, ...updates } = draft;
    const title = (draft.title || "").trim();
    const shortName = (draft.shortName || "").trim();
    // Keep at least one name: if the user cleared both, fall back to a label so
    // the row never renders blank.
    onUpdatePublication(id, {
      ...updates,
      title,
      shortName,
      ...(!title && !shortName ? { title: "Untitled Paper" } : {}),
    });
    cancelEdit();
  };
  const updateDraft = (field, value) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  // ---- delete a single previous attempt ----
  const deleteHistory = (pub, index) => {
    const entry = (pub.history || [])[index];
    const venue = (entry && venueName(entry)) || "this attempt";
    if (
      !window.confirm(
        `Delete the archived attempt at "${venue}"? This cannot be undone.`,
      )
    )
      return;
    const history = (pub.history || []).filter((_, i) => i !== index);
    onUpdatePublication(pub.id, { history });
    if (editing && editing.pubId === pub.id && editing.scope === "attempt")
      cancelEdit();
  };

  // ---- resubmit: add a fresh drafting attempt (the current one drops into
  // history). Because "current" is derived, resolution decides what shows on
  // top: the new draft becomes current unless an Accepted/Published attempt
  // outranks it. ----
  const resubmit = (pub) => {
    const venue = venueName(pub) || "the current venue";
    if (
      !window.confirm(
        `Archive the ${pub.venueType} attempt at "${venue}" into this paper's history and start a fresh submission? Nothing is deleted — the old attempt stays in the collapsible history.`,
      )
    )
      return;
    const archived = {};
    for (const k of ATTEMPT_KEYS) archived[k] = pub[k];
    archived.archivedAt = new Date().toISOString();
    onUpdatePublication(pub.id, {
      ...blankAttempt(),
      status: "drafting",
      revisionNumber: "",
      history: [archived, ...(pub.history || [])],
    });
    setExpanded((prev) => ({ ...prev, [pub.id]: true }));
    setHistoryOpen((prev) => ({ ...prev, [pub.id]: true }));
  };

  // ---- quick status change from read view ----
  const setStatus = (pub, status) => onUpdatePublication(pub.id, { status });

  // ---- search / sort ----
  const matchesSearch = (p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const hay = [
      p.title,
      p.shortName,
      p.authors,
      p.conferenceName,
      p.conferenceLocation,
      p.journalName,
      p.publisher,
      p.comments,
      ...(p.history || []).flatMap((h) => [h.conferenceName, h.journalName]),
    ];
    return hay.some((s) => (s || "").toLowerCase().includes(q));
  };

  const visible = sortPublications(
    publications.filter(
      (p) =>
        matchesSearch(p) &&
        (statusFilter.size === 0 || statusFilter.has(p.status)),
    ),
    sortMode,
  );

  // Stats strip: count per status (only statuses that occur).
  const statusCounts = PUB_STATUSES.map((s) => ({
    ...s,
    count: publications.filter((p) => p.status === s.id).length,
  })).filter((s) => s.count > 0);

  const inputCls =
    "w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-lg border-2 border-gray-600 focus:border-purple-500 outline-none text-sm";

  // ---- edit form ----
  const renderEditForm = (scope = "paper") => {
    const d = draft;
    if (!d) return null;
    const isJournal = d.venueType === "journal";
    const isPaper = scope === "paper";
    return (
      <div className="space-y-3 p-3 bg-gray-700/40 rounded-xl border-2 border-purple-500/50">
        {!isPaper && (
          <p className="text-[11px] text-purple-300/80">
            Editing a previous attempt. Paper-level details (short name, title,
            co-authors, hints, links) are edited from the current attempt.
          </p>
        )}
        {isPaper && (
          <>
            <div>
              <label className="block text-xs text-purple-300 mb-1">
                Short name
              </label>
              <input
                type="text"
                value={d.shortName ?? ""}
                onChange={(e) => updateDraft("shortName", e.target.value)}
                placeholder="e.g. GraphNet"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs text-purple-300 mb-1">
                Full paper title
              </label>
              <input
                type="text"
                value={d.title ?? ""}
                onChange={(e) => updateDraft("title", e.target.value)}
                className={inputCls}
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Fill in at least one of short name / full title.
              </p>
            </div>
            <div>
              <label className="block text-xs text-purple-300 mb-1">
                Co-authors
              </label>
              <input
                type="text"
                value={d.authors ?? ""}
                onChange={(e) => updateDraft("authors", e.target.value)}
                placeholder="Comma-separated names"
                className={inputCls}
              />
            </div>
          </>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-purple-300 mb-1">
              Venue type
            </label>
            <select
              value={d.venueType || "conference"}
              onChange={(e) => updateDraft("venueType", e.target.value)}
              className={inputCls}
            >
              <option value="conference">Conference</option>
              <option value="journal">Journal</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-purple-300 mb-1">Status</label>
            <select
              value={d.status || "drafting"}
              onChange={(e) => updateDraft("status", e.target.value)}
              className={inputCls}
            >
              {PUB_STATUSES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Revision number — only relevant while the paper is under revision */}
        {d.status === "revision" && (
          <div>
            <label className="block text-xs text-purple-300 mb-1">
              Revision number
            </label>
            <input
              type="text"
              value={d.revisionNumber ?? ""}
              onChange={(e) => updateDraft("revisionNumber", e.target.value)}
              placeholder="e.g. R1, Major revision 1"
              className={inputCls}
            />
          </div>
        )}

        {/* Venue-specific details */}
        {!isJournal && (
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="block text-xs text-purple-300 mb-1">
                Conference name
              </label>
              <input
                type="text"
                value={d.conferenceName ?? ""}
                onChange={(e) => updateDraft("conferenceName", e.target.value)}
                placeholder="e.g. NeurIPS 2026"
                className={inputCls}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-purple-300 mb-1">
                Location / venue
              </label>
              <input
                type="text"
                value={d.conferenceLocation ?? ""}
                onChange={(e) =>
                  updateDraft("conferenceLocation", e.target.value)
                }
                placeholder="City, Country"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs text-purple-300 mb-1">
                Conference date from
              </label>
              <input
                type="date"
                value={d.conferenceDateFrom || ""}
                onChange={(e) =>
                  updateDraft("conferenceDateFrom", e.target.value || null)
                }
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs text-purple-300 mb-1">
                Conference date to
              </label>
              <input
                type="date"
                value={d.conferenceDateTo || ""}
                onChange={(e) =>
                  updateDraft("conferenceDateTo", e.target.value || null)
                }
                className={inputCls}
              />
            </div>
          </div>
        )}
        {isJournal && (
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="block text-xs text-purple-300 mb-1">
                Journal name
              </label>
              <input
                type="text"
                value={d.journalName ?? ""}
                onChange={(e) => updateDraft("journalName", e.target.value)}
                placeholder="e.g. IEEE TPAMI"
                className={inputCls}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-purple-300 mb-1">
                Publisher
              </label>
              <input
                type="text"
                value={d.publisher ?? ""}
                onChange={(e) => updateDraft("publisher", e.target.value)}
                placeholder="e.g. IEEE, Elsevier, Springer"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs text-purple-300 mb-1">
                Impact factor
              </label>
              <input
                type="text"
                value={d.impactFactor ?? ""}
                onChange={(e) => updateDraft("impactFactor", e.target.value)}
                placeholder="e.g. 12.3"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs text-purple-300 mb-1">
                Quartile
              </label>
              <select
                value={d.quartile || ""}
                onChange={(e) => updateDraft("quartile", e.target.value)}
                className={inputCls}
              >
                <option value="">—</option>
                <option value="Q1">Q1</option>
                <option value="Q2">Q2</option>
                <option value="Q3">Q3</option>
                <option value="Q4">Q4</option>
              </select>
            </div>
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-purple-300 mb-1">
              Date of submission
            </label>
            <input
              type="date"
              value={d.submittedDate || ""}
              onChange={(e) =>
                updateDraft("submittedDate", e.target.value || null)
              }
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-purple-300 mb-1">
              Decision/Due (shows in Reminders)
            </label>
            <input
              type="date"
              value={d.expectedDecisionDate || ""}
              onChange={(e) =>
                updateDraft("expectedDecisionDate", e.target.value || null)
              }
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-purple-300 mb-1">
              Decision date (acceptance / rejection)
            </label>
            <input
              type="date"
              value={d.decisionDate || ""}
              onChange={(e) =>
                updateDraft("decisionDate", e.target.value || null)
              }
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-purple-300 mb-1">
              Camera-ready deadline (shows in Reminders)
            </label>
            <input
              type="date"
              value={d.cameraReadyDate || ""}
              onChange={(e) =>
                updateDraft("cameraReadyDate", e.target.value || null)
              }
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-purple-300 mb-1">
              Publication date
            </label>
            <input
              type="date"
              value={d.publishedDate || ""}
              onChange={(e) =>
                updateDraft("publishedDate", e.target.value || null)
              }
              className={inputCls}
            />
          </div>
        </div>

        {/* Links (paper-level) */}
        {isPaper && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-purple-300 mb-1">
                DOI / paper URL
              </label>
              <input
                type="text"
                value={d.doiUrl ?? ""}
                onChange={(e) => updateDraft("doiUrl", e.target.value)}
                placeholder="https://doi.org/..."
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs text-purple-300 mb-1">
                Preprint / arXiv link
              </label>
              <input
                type="text"
                value={d.preprintUrl ?? ""}
                onChange={(e) => updateDraft("preprintUrl", e.target.value)}
                placeholder="https://arxiv.org/abs/..."
                className={inputCls}
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs text-purple-300 mb-1">
            Notes for this submission attempt
          </label>
          <textarea
            value={d.attemptNotes ?? ""}
            onChange={(e) => updateDraft("attemptNotes", e.target.value)}
            rows={4}
            placeholder={MARKDOWN_HINT}
            className={`${inputCls} resize-y font-mono`}
          />
        </div>
        {isPaper && (
          <div>
            <label className="block text-xs text-purple-300 mb-1">
              Hints / additional comments (about the paper)
            </label>
            <textarea
              value={d.comments ?? ""}
              onChange={(e) => updateDraft("comments", e.target.value)}
              rows={4}
              placeholder={MARKDOWN_HINT}
              className={`${inputCls} resize-y font-mono`}
            />
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={saveEdit}
            className="flex-1 bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            {isPaper ? "Save paper" : "Save attempt"}
          </button>
          <button
            onClick={cancelEdit}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // ---- read view of the current attempt ----
  const renderAttemptDetails = (a, keyPrefix) => {
    const isJournal = a.venueType === "journal";
    return (
      <>
        <dl className="text-sm space-y-1">
          {isJournal ? (
            <>
              {a.journalName && <Row label="Journal">{a.journalName}</Row>}
              {a.publisher && <Row label="Publisher">{a.publisher}</Row>}
              {a.impactFactor && (
                <Row label="Impact factor">{a.impactFactor}</Row>
              )}
              {a.quartile && <Row label="Quartile">{a.quartile}</Row>}
            </>
          ) : (
            <>
              {a.conferenceName && (
                <Row label="Conference">{a.conferenceName}</Row>
              )}
              {a.conferenceLocation && (
                <Row label="Location">{a.conferenceLocation}</Row>
              )}
              {(a.conferenceDateFrom || a.conferenceDateTo) && (
                <Row label="Conf. dates">
                  {a.conferenceDateFrom ? fmt(a.conferenceDateFrom) : "?"}
                  {a.conferenceDateTo ? ` – ${fmt(a.conferenceDateTo)}` : ""}
                </Row>
              )}
            </>
          )}
          {a.submittedDate && (
            <Row label="Submitted">{fmt(a.submittedDate)}</Row>
          )}
          {a.expectedDecisionDate && (
            <Row label="Decision/Due">
              <span className="text-amber-300">
                {fmt(a.expectedDecisionDate)}
              </span>
            </Row>
          )}
          {a.decisionDate && (
            <Row label="Decision date">{fmt(a.decisionDate)}</Row>
          )}
          {a.cameraReadyDate && (
            <Row label="Camera-ready">
              <span className="text-red-300">{fmt(a.cameraReadyDate)}</span>
            </Row>
          )}
          {a.publishedDate && (
            <Row label="Published">{fmt(a.publishedDate)}</Row>
          )}
        </dl>
        {(a.attemptNotes || "").trim() && (
          <div className="mt-2">
            <div className="text-gray-500 text-sm mb-1">Attempt notes</div>
            <div className="bg-gray-800/50 rounded-lg p-2 max-h-48 overflow-y-auto border border-gray-700">
              {renderMarkdownContent(a.attemptNotes, `${keyPrefix}-notes`)}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderReadView = (pub) => {
    const meta = statusMeta(pub.status);
    const doi = normalizeUrl(pub.doiUrl);
    const preprint = normalizeUrl(pub.preprintUrl);
    const history = pub.history || [];
    return (
      <div className="space-y-3">
        {/* Status + quick transitions */}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-medium border ${meta.badge}`}
          >
            {meta.label}
            {pub.status === "revision" && (pub.revisionNumber || "").trim()
              ? ` · ${pub.revisionNumber.trim()}`
              : ""}
          </span>
          {PUB_STATUSES.filter((s) => s.id !== pub.status).map((s) => (
            <button
              key={s.id}
              onClick={() => setStatus(pub, s.id)}
              className="text-xs px-2 py-0.5 rounded-full border bg-gray-600/30 text-gray-400 border-gray-600/50 hover:bg-gray-600/60 hover:text-gray-200 transition-colors"
              title={`Mark as ${s.label}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {pub.authors && (
          <div className="flex items-start gap-2 text-sm text-gray-300">
            <Users className="w-4 h-4 text-purple-300 flex-shrink-0 mt-0.5" />
            <span className="break-words min-w-0">
              {renderRichText(pub.authors, `pub-auth-${pub.id}`)}
            </span>
          </div>
        )}

        {renderAttemptDetails(pub, `pub-${pub.id}`)}

        {(doi || preprint) && (
          <div className="flex flex-wrap gap-3 text-sm">
            {doi && (
              <a
                href={doi}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-300 hover:underline inline-flex items-center gap-1 break-all"
              >
                DOI / paper <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            )}
            {preprint && (
              <a
                href={preprint}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-300 hover:underline inline-flex items-center gap-1 break-all"
              >
                Preprint <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            )}
          </div>
        )}

        {(pub.comments || "").trim() && (
          <div className="bg-gray-800/50 rounded-lg p-2 max-h-48 overflow-y-auto border border-gray-700">
            {renderMarkdownContent(pub.comments, `pub-com-${pub.id}`)}
          </div>
        )}

        {/* Previous attempts (collapsible, retained forever) */}
        {history.length > 0 && (
          <div className="border border-gray-600/60 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleHistory(pub.id)}
              className="w-full flex items-center gap-2 px-3 py-2 bg-gray-800/60 hover:bg-gray-800 text-sm text-gray-300 transition-colors"
            >
              {historyOpen[pub.id] ? (
                <ChevronDown className="w-4 h-4 text-purple-300" />
              ) : (
                <ChevronRight className="w-4 h-4 text-purple-300" />
              )}
              <History className="w-4 h-4 text-purple-300" />
              Previous attempts ({history.length})
            </button>
            {historyOpen[pub.id] && (
              <div className="divide-y divide-gray-700/60">
                {history.map((h, idx) => {
                  const hm = statusMeta(h.status);
                  const isEditingThisAttempt =
                    editing &&
                    editing.pubId === pub.id &&
                    editing.scope === "attempt" &&
                    editing.index === idx;
                  return (
                    <div
                      key={`${pub.id}-h-${idx}`}
                      className="p-3 bg-gray-800/30"
                    >
                      {isEditingThisAttempt ? (
                        renderEditForm("attempt")
                      ) : (
                        <>
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            {h.venueType === "journal" ? (
                              <Landmark className="w-4 h-4 text-gray-400" />
                            ) : (
                              <GraduationCap className="w-4 h-4 text-gray-400" />
                            )}
                            <span className="text-sm text-gray-300 font-medium">
                              {venueName(h) || "(venue not recorded)"}
                            </span>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium border ${hm.badge}`}
                            >
                              {hm.label}
                              {h.status === "revision" &&
                              (h.revisionNumber || "").trim()
                                ? ` · ${h.revisionNumber.trim()}`
                                : ""}
                            </span>
                            {h.archivedAt && (
                              <span className="text-xs text-gray-500 ml-auto">
                                archived {fmt(h.archivedAt)}
                              </span>
                            )}
                          </div>
                          {renderAttemptDetails(h, `pub-${pub.id}-h-${idx}`)}
                          <div className="flex flex-wrap gap-2 pt-2">
                            <button
                              onClick={() => startEditHistory(pub, idx)}
                              className="flex items-center gap-1 text-xs bg-gray-700 hover:bg-gray-600 text-purple-300 px-2.5 py-1 rounded-lg transition-colors"
                            >
                              <Edit2 className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button
                              onClick={() => deleteHistory(pub, idx)}
                              className="flex items-center gap-1 text-xs bg-gray-700 hover:bg-red-500/20 text-gray-400 hover:text-red-300 px-2.5 py-1 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            onClick={() => startEdit(pub)}
            className="flex items-center gap-1 text-xs bg-gray-700 hover:bg-gray-600 text-purple-300 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Edit2 className="w-3.5 h-3.5" /> Edit
          </button>
          <button
            onClick={() => resubmit(pub)}
            className="flex items-center gap-1 text-xs bg-gray-700 hover:bg-gray-600 text-cyan-300 px-3 py-1.5 rounded-lg transition-colors"
            title="Archive this attempt into history and start a fresh submission (nothing is deleted)"
          >
            <Send className="w-3.5 h-3.5" /> Resubmit to new venue
          </button>
          <button
            onClick={() => {
              if (
                window.confirm(
                  `Delete paper "${headerName(pub)}" including its submission history? This cannot be undone.`,
                )
              ) {
                onDeletePublication(pub.id);
              }
            }}
            className="flex items-center gap-1 text-xs bg-gray-700 hover:bg-gray-600 text-red-300 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>
    );
  };

  // ---- one row per paper ----
  const renderRow = (pub) => {
    const isOpen = expanded[pub.id];
    const isEditingThis =
      editing && editing.pubId === pub.id && editing.scope === "paper";
    const meta = statusMeta(pub.status);
    const venue = venueName(pub);
    const resubmissions = (pub.history || []).length;
    return (
      <div
        key={pub.id}
        className="rounded-xl border-2 bg-gray-700/50 border-gray-600 hover:border-purple-500/50 transition-all overflow-hidden"
      >
        {/* Row header */}
        <div className="flex items-start gap-2 p-3">
          <button
            onClick={() => toggleExpand(pub.id)}
            className="text-gray-400 hover:text-purple-400 transition-colors mt-0.5 flex-shrink-0"
            title={isOpen ? "Collapse" : "Expand"}
          >
            {isOpen ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {pub.venueType === "journal" ? (
                <Landmark className="w-4 h-4 text-purple-300 flex-shrink-0" />
              ) : (
                <GraduationCap className="w-4 h-4 text-purple-300 flex-shrink-0" />
              )}
              <h4 className="font-semibold text-purple-300 break-words min-w-0">
                {headerName(pub)}
              </h4>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium border flex-shrink-0 ${meta.badge}`}
              >
                {meta.label}
                {pub.status === "revision" && (pub.revisionNumber || "").trim()
                  ? ` · ${pub.revisionNumber.trim()}`
                  : ""}
              </span>
              {resubmissions > 0 && (
                <span
                  className="text-xs text-gray-500 flex items-center gap-1 flex-shrink-0"
                  title={`${resubmissions} previous attempt${resubmissions > 1 ? "s" : ""} in history`}
                >
                  <History className="w-3 h-3" /> {resubmissions}
                </span>
              )}
            </div>
            {/* Full paper title (shown below the short name when they differ) */}
            {secondaryTitle(pub) && (
              <p className="mt-0.5 text-sm text-gray-300 break-words">
                {secondaryTitle(pub)}
              </p>
            )}
            {/* Collapsed one-line summary */}
            {!isOpen && (
              <div className="mt-1 text-xs text-gray-400 flex flex-wrap gap-x-4 gap-y-0.5">
                {venue && <span className="text-gray-300">{venue}</span>}
                {pub.authors && (
                  <span className="truncate max-w-[16rem]">{pub.authors}</span>
                )}
                {pub.submittedDate && (
                  <span>Submitted {fmt(pub.submittedDate)}</span>
                )}
                {pub.expectedDecisionDate && (
                  <span className="text-amber-300/80">
                    Decision/Due {fmt(pub.expectedDecisionDate)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        {isOpen && (
          <div className="px-3 pb-3 pl-9">
            {isEditingThis ? renderEditForm() : renderReadView(pub)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gray-800 rounded-3xl p-4 sm:p-6 border-2 border-purple-500/30 shadow-lg">
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <BookOpen className="w-5 h-5 text-purple-400 flex-shrink-0" />
          <h3 className="text-xl font-bold text-purple-400 truncate">
            Publications
          </h3>
          <span className="text-xs text-gray-500">({publications.length})</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {(onMovePanelUp || onMovePanelDown) && (
            <div className="flex items-center gap-1">
              <button
                onClick={onMovePanelUp}
                disabled={isFirstPanel}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move panel up"
              >
                <ChevronUp className="w-4 h-4 text-purple-300" />
              </button>
              <button
                onClick={onMovePanelDown}
                disabled={isLastPanel}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move panel down"
              >
                <ChevronDown className="w-4 h-4 text-purple-300" />
              </button>
            </div>
          )}

          {/* Sort */}
          <div className="relative">
            <button
              onClick={() => setShowSortMenu((v) => !v)}
              className="p-2 bg-gray-700 hover:bg-gray-600 rounded-xl transition-colors flex items-center gap-1"
              title="Sort papers"
            >
              <ArrowUpDown className="w-4 h-4 text-purple-300" />
            </button>
            {showSortMenu && (
              <div className="absolute right-0 top-10 z-30 bg-gray-800 border border-gray-600 rounded-lg shadow-xl py-1 min-w-[11rem]">
                <div className="px-3 py-1 text-xs text-gray-500 uppercase tracking-wide">
                  Sort by
                </div>
                {SORT_MODES.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => {
                      setSortMode(m.id);
                      setShowSortMenu(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      sortMode === m.id
                        ? "bg-purple-500/20 text-purple-300"
                        : "text-gray-300 hover:bg-gray-700"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => {
              setShowAdd((v) => !v);
              setNewTitle("");
              setNewShortName("");
            }}
            className="p-2 bg-purple-500 hover:bg-purple-600 rounded-xl transition-colors"
            title="Add new paper"
          >
            {showAdd ? (
              <X className="w-5 h-5 text-white" />
            ) : (
              <Plus className="w-5 h-5 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Stats strip — clickable status filters. Click one or more to show only
          those; click again to unselect. With none selected, all papers show. */}
      {statusCounts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {statusCounts.map((s) => {
            const active = statusFilter.has(s.id);
            const anyActive = statusFilter.size > 0;
            return (
              <button
                key={s.id}
                onClick={() => toggleStatusFilter(s.id)}
                className={`text-xs px-2.5 py-1 rounded-full font-medium border transition-all ${s.badge} ${
                  active ? "ring-2 ring-purple-400/70" : ""
                } ${anyActive && !active ? "opacity-40 hover:opacity-80" : ""}`}
                title={active ? `Showing ${s.label}` : `Show only ${s.label}`}
              >
                {s.label}: {s.count}
              </button>
            );
          })}
          {statusFilter.size > 0 && (
            <button
              onClick={() => setStatusFilter(new Set())}
              className="text-xs px-2.5 py-1 rounded-full border border-gray-600 text-gray-400 hover:text-gray-200 hover:bg-gray-700 transition-colors"
            >
              Clear filter
            </button>
          )}
        </div>
      )}

      {/* Search */}
      {publications.length > 0 && (
        <div className="relative mb-4">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, author, venue, publisher..."
            className="w-full bg-gray-700 text-cyan-200 pl-9 pr-3 py-2 rounded-lg border-2 border-gray-600 focus:border-purple-500 outline-none text-sm"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Add form */}
      {showAdd && (
        <div className="mb-4 p-4 bg-gray-700/50 rounded-xl border-2 border-purple-500/50 space-y-3">
          <input
            type="text"
            value={newShortName}
            onChange={(e) => setNewShortName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
              else if (e.key === "Escape") setShowAdd(false);
            }}
            placeholder="Short name (e.g. GraphNet)..."
            autoFocus
            className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-lg border-2 border-gray-600 focus:border-purple-500 outline-none"
          />
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
              else if (e.key === "Escape") setShowAdd(false);
            }}
            placeholder="Full paper title..."
            className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-lg border-2 border-gray-600 focus:border-purple-500 outline-none"
          />
          <p className="text-xs text-gray-400">
            Enter a short name, the full title, or both — at least one is
            required. The edit form opens right after so you can fill in
            co-authors, venue, status and dates.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={!newTitle.trim() && !newShortName.trim()}
              className="flex-1 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Add Paper
            </button>
            <button
              onClick={() => setShowAdd(false)}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {publications.length === 0 && !showAdd && (
        <div className="text-center py-8 text-gray-400">
          <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No papers yet. Click the + button to track your first paper.</p>
        </div>
      )}

      {publications.length > 0 && visible.length === 0 && (
        <div className="text-center py-6 text-gray-400 text-sm">
          No papers match{" "}
          {search ? `"${search}"` : "the selected status filter"}.
        </div>
      )}

      {/* Horizontal list: one paper per row */}
      {visible.length > 0 && (
        <div className="space-y-3">{visible.map(renderRow)}</div>
      )}
    </div>
  );
};

// Small label/value row used in the read view.
const Row = ({ label, children }) => (
  <div className="flex gap-2">
    <dt className="text-gray-500 flex-shrink-0 w-32">{label}</dt>
    <dd className="text-gray-300 break-words min-w-0">{children}</dd>
  </div>
);

export default Publications;
